import React, { useState, useEffect } from "react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { Menu } from "lucide-react";
import { TranslationButton } from "@/components/i18n/TranslationButton";
import { useTranslationEdit } from "@/contexts/TranslationEditContext";
import { useSubscriptionAccess } from "@/hooks/useSubscriptionAccess";

import { Logo } from "./Logo";
import { DesktopNavigation } from "./DesktopNavigation";
import { CustomPlanNavigation } from "./CustomPlanNavigation";
import { RestrictedNavigation } from "./RestrictedNavigation";
import { MobileMenu } from "./MobileMenu";
import { CustomPlanMobileMenu } from "./CustomPlanMobileMenu";
import { RestrictedMobileMenu } from "./RestrictedMobileMenu";
import { HeaderActions } from "./HeaderActions";

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const { user, isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isEditMode } = useTranslationEdit();
  const { 
    isLoading: isLoadingSubscription, 
    accessLevel, 
    isCustomPlan,
    hasValidPaidSubscription,
    requiresPayment
  } = useSubscriptionAccess();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Determine which navigation to show
  const getNavigationType = (): 'full' | 'custom' | 'restricted' | 'loading' => {
    if (isLoadingSubscription) return 'loading';
    
    // If subscriptions are disabled, show full navigation
    if (accessLevel === 'subscriptions_disabled') return 'full';
    
    // If user requires payment (no subscription, pending payment, expired)
    if (requiresPayment) return 'restricted';
    
    // If user has a custom plan with valid payment
    if (isCustomPlan && hasValidPaidSubscription) return 'custom';
    
    // If user has a valid paid subscription (standard plans)
    if (hasValidPaidSubscription) return 'full';
    
    // Free tier users get full navigation (limited by feature flags)
    if (accessLevel === 'free') return 'full';
    
    // Default to restricted for safety
    return 'restricted';
  };

  const navigationType = getNavigationType();

  const renderDesktopNavigation = () => {
    switch (navigationType) {
      case 'loading':
        return <div className="h-8" />; // Placeholder while loading
      case 'custom':
        return <CustomPlanNavigation />;
      case 'restricted':
        return <RestrictedNavigation />;
      case 'full':
      default:
        return <DesktopNavigation />;
    }
  };

  const renderMobileMenu = () => {
    switch (navigationType) {
      case 'loading':
        return null;
      case 'custom':
        return <CustomPlanMobileMenu onOpenChange={setMobileMenuOpen} />;
      case 'restricted':
        return <RestrictedMobileMenu onOpenChange={setMobileMenuOpen} />;
      case 'full':
      default:
        return <MobileMenu onOpenChange={setMobileMenuOpen} />;
    }
  };

  return (
    <header className={cn(
      "sticky top-0 z-50 w-full transition-all duration-300",
      "bg-card/95 backdrop-blur-xl border-b border-border/50",
      scrolled ? "shadow-sm" : "",
    )}>
      <div className="container flex h-16 items-center px-4 sm:px-6 lg:px-8">
        <Logo isAuthenticated={isAuthenticated} />
        
        {user && (
          <>
            {/* Desktop Navigation */}
            <div className="hidden md:flex flex-1 items-center justify-center">
              {renderDesktopNavigation()}
            </div>
            
            {/* Mobile Menu Button & Sheet */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" disabled={isEditMode}>
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] sm:w-[350px]">
                {renderMobileMenu()}
              </SheetContent>
            </Sheet>
          </>
        )}
        
        <div className="flex flex-1 items-center justify-between space-x-2 md:justify-end">
          <div className="w-full flex-1 md:w-auto md:flex-none">
            {/* Searchbar would go here */}
          </div>
          <HeaderActions authenticated={isAuthenticated} />
        </div>
      </div>
    </header>
  );
}
